import { type AddressType } from '~/types/Address';
import { type InsuranceSettings } from '~/types/Insurance';
import { type OrderObjectId, type OrderSelection } from '~/types/Order';
import { type TransactionStatus } from '~/types/Transaction';

type TrackingEventObject = {
  name: string;
  operations: Record<string, string>;
};

const TRACKING_OPERATIONS = {
  ADDRESS_ADDED: 'added',
  ADDRESS_BOOK_CREATED: 'created',
  ADDRESS_BOOK_DELETED: 'deleted',
  ADDRESS_BOOK_EDITED: 'edited',
  ADDRESS_BOOK_QUERIED: 'queried',
  ADDRESS_BOOK_SAVE_ATTEMPTED: 'save attempted',

  ADDRESS_BOOK_SELECTED: 'selected',
  ADDRESS_CREATED: 'created',
  ADDRESS_DELETED: 'deleted',
  ADDRESS_SAVE_ATTEMPTED: 'save attempted',
  ADDRESS_UPDATED: 'updated',
  ADDRESS_VIEWED: 'viewed',
  API_TOKEN_COPIED: 'copied',
  API_TOKEN_DELETED: 'deleted',

  API_TOKEN_GENERATED: 'generated',
  BULK_BAR_COMPLETED: 'completed',

  BULK_BAR_SELECTED: 'selected',
  BULK_BAR_VIEWED: 'viewed',

  CARRIER_ACCOUNT_ACTIVATED: 'activated',

  CARRIER_ACCOUNT_ADDED: 'added',
  CARRIER_ACCOUNT_CLOSED: 'closed',
  CARRIER_ACCOUNT_DEACTIVATED: 'deactivated',
  CARRIER_ACCOUNT_EDITED: 'edited',
  CARRIER_ACCOUNT_SAVE_ATTEMPTED: 'save attempted',
  CARRIER_ACCOUNT_SELECTED: 'selected',

  CARRIER_ACCOUNT_STARTED: 'started',
  DEFAULT_LABEL_SETTINGS_UPDATED: 'saved',
  DEFAULT_UPDATED_INTERNATIONAL: 'international',
  DEFAULT_UPDATED_LABEL: 'label',
  IBD_MODAL_ACCCEPT: 'accept',
  IBD_MODAL_CHECKED: 'checkbox',
  IBD_MODAL_CLICKED_OUT: 'clicked out',
  IBD_MODAL_CLOSED: 'closed',
  IBD_MODAL_LEARN_MORE: 'Learn More',
  IBD_MODAL_LOADED: 'loaded',
  IBD_MODAL_NOT_LOADED: 'not_loaded',
  IBD_MODAL_TC: 'TC',
  INSURANCE_PROTECT_SHIPMENT_ORDER_DETAIL: 'Protect shipment order detail',

  INSURANCE_PROTECT_SHIPMENT_SIDE_PANEL: 'Protect shipment side panel',
  ITEM_ADDED: 'added',
  ITEM_DELETED: 'deleted',

  ITEM_EDITED: 'edited',
  ITEM_SAVED: 'saved',
  ITEM_VIEWED: 'viewed',
  LABEL_DOWNLOADED: 'downloaded',
  LABEL_SAVE_ATTEMPTED: 'save attempted',

  LABEL_TRANSACTION_COMPLETED: 'transaction completed',
  MANIFEST_DOWNLOADED: 'downloaded',
  MANIFEST_GENERATED: 'generated',

  MANIFEST_UPDATED: 'updated',
  ORDER_ADDED: 'added',
  ORDER_DESELECTED: 'deselected',
  ORDER_DUPLICATED: 'duplicated',
  ORDER_IMPORT_STARTED: 'import started',

  ORDER_UPDATED: 'updated',
  ORDER_VIEWED: 'viewed',
  PACKAGE_ADDED: 'added',

  PACKAGE_BROWSED: 'browsed',
  PACKAGE_CREATED: 'created',
  PACKAGE_DELETED: 'deleted',
  PACKAGE_SAVE_ATTEMPTED: 'save attempted',
  PACKAGE_SEARCHED: 'searched',
  PACKAGE_UPDATED: 'updated',
  PARCEL_BOT_BACK: 'back',

  PARCEL_BOT_CLOSED: 'closed',
  PARCEL_BOT_LOADED: 'loaded',
  PARCEL_BOT_QUERIED: 'queried',
  PARCEL_BOT_RETURNED: 'returned',
  PAYMENT_METHOD_ADDED: 'added',
  PAYMENT_METHOD_SAVE_ATTEMPTED: 'save attempted',

  PAYMENT_METHOD_UPDATED: 'updated',
  PAYMENT_METHOD_VIEWED: 'viewed',
  RATE_DISPLAYED: 'displayed',

  RATE_GENERATED: 'generated',
  RATE_SELECTED: 'selected',

  RETURN_LABEL_CREATED: 'created',

  RETURN_LABEL_UPDATED: 'updated',
  STORE_CONNECTION_QUERIED: 'queried',
} as const;

export const ADDRESS_TRACKING_EVENT = {
  name: 'Address Updated',
  operations: {
    ADDED: TRACKING_OPERATIONS.ADDRESS_ADDED,
    CREATED: TRACKING_OPERATIONS.ADDRESS_CREATED,
    DELETED: TRACKING_OPERATIONS.ADDRESS_DELETED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.ADDRESS_SAVE_ATTEMPTED,
    UPDATED: TRACKING_OPERATIONS.ADDRESS_UPDATED,
    VIEWED: TRACKING_OPERATIONS.ADDRESS_VIEWED,
  },
} as const satisfies TrackingEventObject;

export const CARRIER_ACCOUNT_TRACKING_EVENT = {
  name: 'Carrier Account Updated',
  operations: {
    ACTIVATED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_ACTIVATED,
    ADDED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_ADDED,
    CLOSED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_CLOSED,
    DEACTIVATED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_DEACTIVATED,
    EDITED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_EDITED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_SAVE_ATTEMPTED,
    SELECTED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_SELECTED,
    STARTED: TRACKING_OPERATIONS.CARRIER_ACCOUNT_STARTED,
  },
} as const satisfies TrackingEventObject;

export const DEFAULT_UPDATED_EVENT = {
  name: 'Default Updated',
  operations: {
    INTERNATIONAL: TRACKING_OPERATIONS.DEFAULT_UPDATED_INTERNATIONAL,
    LABEL: TRACKING_OPERATIONS.DEFAULT_UPDATED_LABEL,
  },
} as const satisfies TrackingEventObject;

// New defaults experience, separate event  for saving shipping options
export const DEFAULT_LABEL_SETTINGS_UPDATED_EVENT = {
  name: 'Default Label Settings Updated',
  operations: {
    SAVED: TRACKING_OPERATIONS.DEFAULT_LABEL_SETTINGS_UPDATED,
  },
} as const satisfies TrackingEventObject;

export const ORDER_TRACKING_EVENT = {
  name: 'Order Updated',
  operations: {
    ADDED: TRACKING_OPERATIONS.ORDER_ADDED,
    DESELECTED: TRACKING_OPERATIONS.ORDER_DESELECTED,
    DUPLICATED: TRACKING_OPERATIONS.ORDER_DUPLICATED,
    IMPORT_STARTED: TRACKING_OPERATIONS.ORDER_IMPORT_STARTED,
    UPDATED: TRACKING_OPERATIONS.ORDER_UPDATED,
    VIEWED: TRACKING_OPERATIONS.ORDER_VIEWED,
  },
} as const satisfies TrackingEventObject;

export const LABEL_PURCHASED_EVENT = {
  name: 'Label Purchased',
  operations: {
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.LABEL_SAVE_ATTEMPTED,
    TRANSACTION_COMPLETED: TRACKING_OPERATIONS.LABEL_TRANSACTION_COMPLETED,
  },
} as const satisfies TrackingEventObject;

export const LABEL_UPDATED_EVENT = {
  name: 'Label Updated',
  operations: {
    DOWNLOADED: TRACKING_OPERATIONS.LABEL_DOWNLOADED,
  },
} as const satisfies TrackingEventObject;

export const PACKAGE_TRACKING_EVENT = {
  name: 'Package Updated',
  operations: {
    ADDED: TRACKING_OPERATIONS.PACKAGE_ADDED,
    BROWSED: TRACKING_OPERATIONS.PACKAGE_BROWSED,
    CREATED: TRACKING_OPERATIONS.PACKAGE_CREATED,
    DELETED: TRACKING_OPERATIONS.PACKAGE_DELETED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.PACKAGE_SAVE_ATTEMPTED,
    SEARCHED: TRACKING_OPERATIONS.PACKAGE_SEARCHED,
    UPDATED: TRACKING_OPERATIONS.PACKAGE_UPDATED,
  },
} as const satisfies TrackingEventObject;

export const PAYMENT_METHOD_EVENT = {
  name: 'Payment Method Updated',
  operations: {
    ADDED: TRACKING_OPERATIONS.PAYMENT_METHOD_ADDED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.PAYMENT_METHOD_SAVE_ATTEMPTED,
    UPDATED: TRACKING_OPERATIONS.PAYMENT_METHOD_UPDATED,
    VIEWED: TRACKING_OPERATIONS.PAYMENT_METHOD_VIEWED,
  },
} as const satisfies TrackingEventObject;

export const RATES_UPDATED_EVENT = {
  name: 'Rates Updated',
  operations: {
    GENERATED: TRACKING_OPERATIONS.RATE_GENERATED,
    SELECTED: TRACKING_OPERATIONS.RATE_SELECTED,
  },
} as const satisfies TrackingEventObject;

export const RATE_DISPLAYED_EVENT = {
  name: 'Rate Displayed',
  operations: {
    DISPLAYED: TRACKING_OPERATIONS.RATE_DISPLAYED,
  },
} as const satisfies TrackingEventObject;

export const RETURN_BATCH_ENTRY_CREATION_EVENT = {
  name: 'Return Label Creation',
  operations: {
    CREATED: TRACKING_OPERATIONS.RETURN_LABEL_CREATED,
    UPDATED: TRACKING_OPERATIONS.RETURN_LABEL_UPDATED,
  },
} as const satisfies TrackingEventObject;

export const ITEM_UPDATED_EVENT = {
  name: 'Item Updated',
  operations: {
    ADDED: TRACKING_OPERATIONS.ITEM_ADDED,
    DELETED: TRACKING_OPERATIONS.ITEM_DELETED,
    EDITED: TRACKING_OPERATIONS.ITEM_EDITED,
    SAVED: TRACKING_OPERATIONS.ITEM_SAVED,
    VIWED: TRACKING_OPERATIONS.ITEM_VIEWED,
  },
} as const satisfies TrackingEventObject;

export const MANIFEST_TRACKING_EVENT = {
  name: 'Manifest Updated',
  operations: {
    DOWNLOADED: TRACKING_OPERATIONS.MANIFEST_DOWNLOADED,
    GENERATED: TRACKING_OPERATIONS.MANIFEST_GENERATED,
    UPDATED: TRACKING_OPERATIONS.MANIFEST_UPDATED,
  },
} as const satisfies TrackingEventObject;

export const PARCEL_BOT_TRACKING_EVENT = {
  name: 'Parcel Bot Updated',
  operations: {
    BACK: TRACKING_OPERATIONS.PARCEL_BOT_BACK,
    CLOSED: TRACKING_OPERATIONS.PARCEL_BOT_CLOSED,
    LOADED: TRACKING_OPERATIONS.PARCEL_BOT_LOADED,
    QUERIED: TRACKING_OPERATIONS.PARCEL_BOT_QUERIED,
    RETURNED: TRACKING_OPERATIONS.PARCEL_BOT_RETURNED,
  },
} as const satisfies TrackingEventObject;

export const BULK_BAR_TRACKING_EVENT = {
  name: 'Bulk Bar Updated',
  operations: {
    COMPLETED: TRACKING_OPERATIONS.BULK_BAR_COMPLETED,
    SELECTED: TRACKING_OPERATIONS.BULK_BAR_SELECTED,
    VIEWED: TRACKING_OPERATIONS.BULK_BAR_VIEWED,
  },
} as const satisfies TrackingEventObject;

export const IBD_MODAL_TRACKING_EVENT = {
  name: 'IBD Modal Loaded',
  operations: {
    ACCEPT: TRACKING_OPERATIONS.IBD_MODAL_ACCCEPT,
    CHECKBOX: TRACKING_OPERATIONS.IBD_MODAL_CHECKED,
    CLICKED_OUT: TRACKING_OPERATIONS.IBD_MODAL_CLICKED_OUT,
    CLOSED: TRACKING_OPERATIONS.IBD_MODAL_CLOSED,
    LEARN_MORE: TRACKING_OPERATIONS.IBD_MODAL_LEARN_MORE,
    LOADED: TRACKING_OPERATIONS.IBD_MODAL_LOADED,
    TC: TRACKING_OPERATIONS.IBD_MODAL_TC,
  },
} as const satisfies TrackingEventObject;

export const RECIPIENT_ADDRESS_BOOK_UPDATED = {
  name: 'Recipient Address Book Updated',
  operations: {
    CREATED: TRACKING_OPERATIONS.ADDRESS_BOOK_CREATED,
    DELETED: TRACKING_OPERATIONS.ADDRESS_BOOK_DELETED,
    EDITED: TRACKING_OPERATIONS.ADDRESS_BOOK_EDITED,
    SAVE_ATTEMPTED: TRACKING_OPERATIONS.ADDRESS_BOOK_SAVE_ATTEMPTED,
  },
} as const satisfies TrackingEventObject;

export const RECIPIENT_ADDRESS_BOOK_QUERIED = {
  name: 'Recipient Address Book Queried',
  operations: {
    QUERIED: TRACKING_OPERATIONS.ADDRESS_BOOK_QUERIED,
    SELECTED: TRACKING_OPERATIONS.ADDRESS_BOOK_SELECTED,
  },
} as const satisfies TrackingEventObject;

export const API_TOKEN_UPDATED = {
  name: 'Token Updated',
  operations: {
    COPIED: TRACKING_OPERATIONS.API_TOKEN_COPIED,
    DELETED: TRACKING_OPERATIONS.API_TOKEN_DELETED,
    GENERATED: TRACKING_OPERATIONS.API_TOKEN_GENERATED,
  },
} as const satisfies TrackingEventObject;

export const STORE_CONNECTION_SETTINGS = {
  name: 'Store Connection Settings',
  operations: {
    QUERIED: TRACKING_OPERATIONS.STORE_CONNECTION_QUERIED,
  },
} as const satisfies TrackingEventObject;

export const INSURANCE_UPSELL_EVENT = {
  name: 'Insurance Experiment',
  operations: {
    PROTECT_SHIPMENT_ORDER_DETAIL: TRACKING_OPERATIONS.INSURANCE_PROTECT_SHIPMENT_ORDER_DETAIL,
    PROTECT_SHIPMENT_SIDE_PANEL: TRACKING_OPERATIONS.INSURANCE_PROTECT_SHIPMENT_SIDE_PANEL,
  },
} as const satisfies TrackingEventObject;

const TRACKING_EVENTS = {
  [ADDRESS_TRACKING_EVENT.name]: ADDRESS_TRACKING_EVENT,
  [API_TOKEN_UPDATED.name]: API_TOKEN_UPDATED,
  [BULK_BAR_TRACKING_EVENT.name]: BULK_BAR_TRACKING_EVENT,
  [CARRIER_ACCOUNT_TRACKING_EVENT.name]: CARRIER_ACCOUNT_TRACKING_EVENT,
  [DEFAULT_LABEL_SETTINGS_UPDATED_EVENT.name]: DEFAULT_LABEL_SETTINGS_UPDATED_EVENT,
  [DEFAULT_UPDATED_EVENT.name]: DEFAULT_UPDATED_EVENT,
  [IBD_MODAL_TRACKING_EVENT.name]: IBD_MODAL_TRACKING_EVENT,
  [INSURANCE_UPSELL_EVENT.name]: INSURANCE_UPSELL_EVENT,
  [ITEM_UPDATED_EVENT.name]: ITEM_UPDATED_EVENT,
  [LABEL_PURCHASED_EVENT.name]: LABEL_PURCHASED_EVENT,
  [LABEL_UPDATED_EVENT.name]: LABEL_UPDATED_EVENT,
  [MANIFEST_TRACKING_EVENT.name]: MANIFEST_TRACKING_EVENT,
  [ORDER_TRACKING_EVENT.name]: ORDER_TRACKING_EVENT,
  [PACKAGE_TRACKING_EVENT.name]: PACKAGE_TRACKING_EVENT,
  [PARCEL_BOT_TRACKING_EVENT.name]: PARCEL_BOT_TRACKING_EVENT,
  [PAYMENT_METHOD_EVENT.name]: PAYMENT_METHOD_EVENT,
  [RATE_DISPLAYED_EVENT.name]: RATE_DISPLAYED_EVENT,
  [RATES_UPDATED_EVENT.name]: RATES_UPDATED_EVENT,
  [RECIPIENT_ADDRESS_BOOK_QUERIED.name]: RECIPIENT_ADDRESS_BOOK_QUERIED,
  [RECIPIENT_ADDRESS_BOOK_UPDATED.name]: RECIPIENT_ADDRESS_BOOK_UPDATED,
  [RETURN_BATCH_ENTRY_CREATION_EVENT.name]: RETURN_BATCH_ENTRY_CREATION_EVENT,
  [STORE_CONNECTION_SETTINGS.name]: STORE_CONNECTION_SETTINGS,
} as const satisfies Record<string, TrackingEventObject>;

export type TrackingEventName = keyof typeof TRACKING_EVENTS;

export type RequiredTrackingProps = {
  [ADDRESS_TRACKING_EVENT.name]: {
    addressId: number | undefined;
    addressType: AddressType | undefined;
  };
  [API_TOKEN_UPDATED.name]: {
    environment: 'prod' | 'test';
    isMaximum: boolean;
    tokenId: string;
  };
  [BULK_BAR_TRACKING_EVENT.name]: {
    action:
      | 'add_packing_slip_note'
      | 'bulk_buy'
      | 'copy_tracking'
      | 'download_packing_slip'
      | 'edit_address'
      | 'edit_insurance_and_signature'
      | 'edit_package'
      | 'edit_rates'
      | 'hide_order'
      | 'hide_shipment'
      | 'show_bulk_bar'
      | 'view_items'
      | 'view_selection_only';
    fulfillment_type: 'fulfilled' | 'unfulfilled';
    orders_selected: number | undefined;
  };
  [CARRIER_ACCOUNT_TRACKING_EVENT.name]: {
    carrierAccountId: string | undefined;
    carrierName: string | undefined;
    carrierType: 'BYOA' | 'Shippo Master' | undefined;
  };
  [DEFAULT_LABEL_SETTINGS_UPDATED_EVENT.name]: Record<string, never>;
  [DEFAULT_UPDATED_EVENT.name]: Record<string, never>;
  [IBD_MODAL_TRACKING_EVENT.name]: Record<string, never>;
  [INSURANCE_UPSELL_EVENT.name]: Record<string, never>;
  [ITEM_UPDATED_EVENT.name]: Record<string, never>;
  [LABEL_PURCHASED_EVENT.name]: Record<string, never>;
  [LABEL_UPDATED_EVENT.name]: {
    type: LabelDownloadedTrackingType | undefined;
  };
  [MANIFEST_TRACKING_EVENT.name]: Record<string, never>;
  [ORDER_TRACKING_EVENT.name]: Record<string, never>;
  [PACKAGE_TRACKING_EVENT.name]: {
    isAutoSave: boolean | undefined;
    isOrderDetailsAutoSave?: boolean | undefined;
    isQuickSelect: boolean | undefined;
    parcelId: string | undefined;
  };
  [PARCEL_BOT_TRACKING_EVENT.name]: {
    answer_feedback: '+1' | '-1' | undefined;
    answer_feedback_query_id: string | undefined;
    is_error: boolean | undefined;
    query: string | undefined;
    query_id: string | undefined;
  };
  [PAYMENT_METHOD_EVENT.name]: Record<string, never>; // TODO: Do we need a more specific location?
  [RATE_DISPLAYED_EVENT.name]: Record<string, never>;
  [RATES_UPDATED_EVENT.name]: {
    rateIds: string[] | undefined;
  };

  [RECIPIENT_ADDRESS_BOOK_QUERIED.name]: Record<string, never>;

  [RECIPIENT_ADDRESS_BOOK_UPDATED.name]: Record<string, never>;
  [RETURN_BATCH_ENTRY_CREATION_EVENT.name]: Record<string, never>;
  [STORE_CONNECTION_SETTINGS.name]: Record<string, never>;
};

export type TrackingAppLocation =
  | 'automations'
  | 'bulk_bar'
  | 'download'
  | 'help'
  | 'legacy_mobile_orders'
  | 'manifests'
  | 'onboarding'
  | 'order_details'
  | 'order_details_mui'
  | 'order_table'
  | 'other'
  | 'rates_at_checkout'
  | 'settings'
  | 'shipments'
  | 'side_panel';

export interface TrackingReference {
  appLocation: TrackingAppLocation;
  appVersionName: 'blackbeard' | 'classic';
  initialEventName: TrackingEventName;
  isBulk?: boolean;
  orderId?: OrderObjectId;
  orderIds?: OrderObjectId[];
  orderQuantity?: number;
  referenceId: string;
  selectedOrders?: OrderSelection;
  shipmentId?: number;
  trackedOrderDetailsByShipment?: Record<number, TrackedOrderDetails>;
  userDetails?: UserDetails;
}

export type ExtendedTrackingReference<E extends TrackingEventName> = {
  initialEventName: E;
} & RequiredTrackingProps[E] &
  TrackingReference;

export type OperationsForEvent<E extends TrackingEventName> =
  (typeof TRACKING_EVENTS)[E]['operations'][keyof (typeof TRACKING_EVENTS)[E]['operations']];

type ManifestTrackingProps = {
  carrier: string;
  carrierAccountId: string;
  manifestLabelCount: number;
  senderAddressId: number;
  // ISO 8601
  submissionDate: string;
};

type RequiredOperationProps = {
  [DEFAULT_UPDATED_EVENT.name]: {
    [DEFAULT_UPDATED_EVENT.operations.INTERNATIONAL]: InternationalDefaultsTrackedProps;
    [DEFAULT_UPDATED_EVENT.operations.LABEL]: DefaultLabelTrackedProps;
  };
  [ITEM_UPDATED_EVENT.name]: {
    [ITEM_UPDATED_EVENT.operations.DELETED]: {
      itemId: string | undefined;
    };
    [ITEM_UPDATED_EVENT.operations.EDITED]: {
      itemId: string | undefined;
    };
    [ITEM_UPDATED_EVENT.operations.VIWED]: {
      itemId: string | undefined;
    };
  };
  [LABEL_PURCHASED_EVENT.name]: {
    [LABEL_PURCHASED_EVENT.operations.SAVE_ATTEMPTED]: {
      rateIds: string[];
    };
    [LABEL_PURCHASED_EVENT.operations.TRANSACTION_COMPLETED]: {
      rateIds: string[];
      status: 'mixed' | 'none' | TransactionStatus;
      transactionIds: string[];
    };
  };
  [MANIFEST_TRACKING_EVENT.name]: {
    [MANIFEST_TRACKING_EVENT.operations.DOWNLOADED]: {
      carrier: string;
      manifestId: number;
    };
    [MANIFEST_TRACKING_EVENT.operations.GENERATED]: ManifestTrackingProps;
    [MANIFEST_TRACKING_EVENT.operations.UPDATED]: ManifestTrackingProps;
  };
};

export interface DefaultLabelTrackedProps {
  outboundDefaultServiceLevel1?: string;
  outboundDefaultServiceLevel2?: string;
  outboundDefaultServiceLevelIsMasterAccount?: boolean;
  outboundDefaultServiceLevelIsTest?: boolean;
  packingSlipIncludeOrderNumber?: boolean;
  packingSlipIncludePricing?: boolean;
  packingSlipStorePolicy?: string;
  packingSlipTextOnLabel?: string;
  printLabelFormat?: string;
  printOrder?: string;
  printPackingSlipFormat?: string;
  returnAutomaticOn?: boolean;
  returnDefaultServiceLevel1?: string;
  returnDefaultServiceLevel2?: string;
  returnDefaultServiceLevel2IsMasterAccount?: boolean;
  returnDefaultServiceLevel2IsTest?: boolean;
}

export interface InternationalDefaultsTrackedProps {
  incotermDefault?: string;
  shipmentPurposeDefault?: string;
  taxIdDefault?: boolean;
}

export interface TrackedServiceLevelInfo {
  carrier: string;
  isMasterAccount: boolean;
  isTest: boolean;
  serviceLevel: string;
}

export type LabelDownloadedTrackingType = 'both' | 'packing_slip' | 'shipping_label';

export type RequiredPropsForOperation<
  E extends TrackingEventName,
  O extends OperationsForEvent<E>,
> = E extends keyof RequiredOperationProps
  ? O extends keyof RequiredOperationProps[E]
    ? RequiredOperationProps[E][O]
    : never
  : never;

export type UserDetails = {
  defaultInsuranceSetings?: InsuranceSettings;
  defaultParcelTemplate?: string;
  defaultReturnAddress?: number;
  defaultSenderAddress?: number;
  paymentType: string;
};

export type TrackedOrderDetails = {
  addressRecipient?: string;
  addressReturn?: string;
  addressReturnDefaultSet?: boolean;
  addressSender?: string;
  addressSenderDefaultSet?: boolean;
  carrier?: string;
  hasInsuranceOption?: boolean;
  hasReturnOption?: boolean;
  hasSignatureOption?: boolean;
  insuranceDefaultSet?: boolean;
  parcel?: string;
  parcelCarrierTemplate?: string;
  parcelDefaultSet?: boolean;
  parcelUserTemplate?: string;
  paymentMethod?: string;
  paymentMethodDefaultSet?: boolean;
  return?: string;
  returnDefaultSet?: boolean;
  serviceLevel?: string;
  serviceLevelDefault?: string;
  signature?: boolean;
};

export interface TrackedServiceLevelInfo {
  carrier: string;
  isMasterAccount: boolean;
  isTest: boolean;
  serviceLevel: string;
}

export type AllTrackedProps = DefaultLabelTrackedProps & TrackingReference;
